import React, { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField,
  useMediaQuery, useTheme,
} from '@mui/material';
import * as api from '../plannerApi';
import * as T from '../types';
import { number, parseNumber, today } from '../labels';
import { useLoadInto } from '../common';

/** The freezers (places and their zones) a batch can be kept in, places first. */
export function useFreezers(open: boolean) {
  const [freezers, setFreezers] = useState<T.StorageLocation[] | null>(null);
  useLoadInto(
    open ? () => api.getStorageLocations().then(list => list.filter(l => l.isFreezer), () => []) : null,
    setFreezers, [open]);
  return freezers;
}

/** "Onde fica": one of the freezers. */
export function FreezerSelect({ freezers, value, onChange }: {
  freezers: T.StorageLocation[] | null; value: string; onChange: (id: string) => void;
}) {
  const none = freezers !== null && freezers.length === 0;
  return (
    <TextField
      select
      label="Onde fica"
      value={freezers === null ? '' : value}
      onChange={e => onChange(e.target.value)}
      disabled={freezers === null || none}
      helperText={freezers === null ? 'A carregar…' : none ? 'Nenhum local é congelador: marca um em Em casa › Locais.' : undefined}
    >
      {(freezers ?? []).map(l => (
        <MenuItem key={l.publicId} value={l.publicId} sx={{ pl: l.parentPublicId ? 4 : 2 }}>{l.fullName}</MenuItem>
      ))}
    </TextField>
  );
}

/** Adds something already in the freezer (`batch` null) or changes one added by hand. */
export function FrozenBatchDialog({ open, batch, onClose, onSaved }: {
  open: boolean; batch: T.FrozenBatch | null; onClose: () => void; onSaved: () => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [recipes, setRecipes] = useState<T.RecipeSummary[] | null>(null);
  const [recipeId, setRecipeId] = useState('');
  const [servingsText, setServingsText] = useState('4');
  const [frozenOn, setFrozenOn] = useState(today());
  const [note, setNote] = useState('');
  const [locationId, setLocationId] = useState('');
  const freezers = useFreezers(open);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Where it is, or the first freezer for a new one
  useEffect(() => {
    if (!open || freezers === null) return;
    setLocationId(batch?.locationPublicId ?? freezers.find(l => !l.parentPublicId)?.publicId ?? '');
  }, [open, batch, freezers]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setRecipeId(batch?.recipePublicId ?? '');
    setServingsText(batch ? number(batch.servings, 2) : '4');
    setFrozenOn(batch?.frozenOn ?? today());
    setNote(batch?.note ?? '');
  }, [open, batch]);

  // Dishes: side dishes are made fresh. A failure leaves the list empty and says why.
  const loadRecipes = () => api.getRecipes().then(
    list => ({ list: list.filter(r => r.course !== 'Side' || r.publicId === batch?.recipePublicId), failure: null as string | null }),
    (e: any) => ({ list: [] as T.RecipeSummary[], failure: (e.message ?? 'Ocorreu um erro.') as string | null }));
  useLoadInto(open ? loadRecipes : null, ({ list, failure }) => {
    setRecipes(list);
    if (failure) setError(failure);
  }, [open, batch]);

  const save = async () => {
    setError(null);
    if (!recipeId) { setError('Escolhe a receita.'); return; }
    const servings = parseNumber(servingsText);
    if (servings === null || servings <= 0) { setError('As doses devem ser um número positivo.'); return; }
    if (!frozenOn) { setError('Indica o dia em que foi congelado.'); return; }
    if (!locationId) { setError('Escolhe em que congelador fica.'); return; }
    const request: T.SaveFrozenBatchRequest = { recipePublicId: recipeId, servings, frozenOn, note: note.trim() || null, locationPublicId: locationId };
    setBusy(true);
    try {
      if (batch) await api.updateFrozenBatch(batch.publicId, request);
      else await api.createFrozenBatch(request);
      onSaved();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="xs">
      <DialogTitle>{batch ? batch.recipeName : 'Juntar ao congelador'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label="Receita"
            value={recipes === null ? '' : recipeId}
            onChange={e => setRecipeId(e.target.value)}
            disabled={recipes === null}
            helperText={recipes === null ? 'A carregar…' : undefined}
          >
            {(recipes ?? []).map(r => <MenuItem key={r.publicId} value={r.publicId}>{r.name}</MenuItem>)}
          </TextField>
          <TextField label="Doses" value={servingsText} onChange={e => setServingsText(e.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
          <FreezerSelect freezers={freezers} value={locationId} onChange={setLocationId} />
          <TextField label="Congelado em" type="date" value={frozenOn} onChange={e => setFrozenOn(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField
            label="Nota (opcional)"
            placeholder="Ex.: caixa azul, gaveta de cima"
            value={note}
            onChange={e => setNote(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
